import { Env } from "./Env";

/**
 * Resolves a stored image path against the image API host.
 *
 * Rows and records carry the path alone (`/image/...`) because the host differs
 * per build, so it is never stored alongside the data. An absent path resolves
 * to null rather than a bare host URL, so a row with no image lands on the
 * caller's "no image" branch.
 */
export function toImageApiUrl(path: string): string;
export function toImageApiUrl(path: string | null | undefined): string | null;
export function toImageApiUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  return `${Env.VITE_IMAGE_API_ENDPOINT}${path}`;
}

/**
 * Returns the thumbnail variant of an image-service download URL (INS-1559).
 *
 * The image API serves a 640px WebP thumbnail at a `/thumb` path suffix on every
 * download route, falling back server-side to the original bytes when a thumb has
 * not been generated yet — so callers can adopt thumb URLs before the corpus
 * backfill finishes, with no "not generated yet" error handling.
 *
 * The variant is a **path segment, not a query param**: `useSecureImageSrc`
 * appends `?v=${authGen}` on the WorkOS path, so a `?size=` param would produce a
 * double-`?` URL. Keep it a path segment.
 *
 * - Appends `/thumb` to the URL.
 * - Idempotent: a URL already ending in `/thumb` is returned unchanged.
 * - Passes `null` / `undefined` through unchanged, so it drops in at call sites
 *   whose `downloadUrl` is typed `string | null | undefined`:
 *   `useSecureImageSrc({ downloadUrl: toThumbUrl(downloadUrl), demoUrl })`.
 */
export function toThumbUrl(downloadUrl: string): string;
export function toThumbUrl(
  downloadUrl: string | null | undefined,
): string | null | undefined;
export function toThumbUrl(
  downloadUrl: string | null | undefined,
): string | null | undefined {
  if (downloadUrl == null) return downloadUrl;
  if (downloadUrl.endsWith("/thumb")) return downloadUrl;
  return `${downloadUrl}/thumb`;
}
